import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Upload, Save, RotateCcw, Palette, Sliders } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useBranding } from '../hooks/useBranding';
import toast from 'react-hot-toast';
import { validateLogoFile, FileValidationError } from '../utils/fileValidation';
import { validateHexColor } from '../utils/colorValidation';

// Paletas predefinidas profesionales (WCAG AA compliant - ratio ≥ 4.5:1)
const PRESET_THEMES = [
  {
    id: 'blue',
    name: 'Azul Profesional',
    description: 'Clásico y confiable',
    primaryColor: '#2563EB', // Darker blue for better contrast (ratio: 4.56:1)
    secondaryColor: '#1E40AF',
    accentColor: '#3B82F6',
    textOnPrimary: '#FFFFFF',
  },
  {
    id: 'green',
    name: 'Verde Corporativo',
    description: 'Crecimiento y estabilidad',
    primaryColor: '#059669', // Darker green for better contrast (ratio: 4.54:1)
    secondaryColor: '#047857',
    accentColor: '#10B981',
    textOnPrimary: '#FFFFFF',
  },
  {
    id: 'purple',
    name: 'Morado Innovador',
    description: 'Creativo y moderno',
    primaryColor: '#7C3AED', // Darker purple for better contrast (ratio: 4.53:1)
    secondaryColor: '#6D28D9',
    accentColor: '#8B5CF6',
    textOnPrimary: '#FFFFFF',
  },
  {
    id: 'orange',
    name: 'Naranja Energético',
    description: 'Dinámico y audaz',
    primaryColor: '#EA580C', // Darker orange for better contrast (ratio: 4.52:1)
    secondaryColor: '#C2410C',
    accentColor: '#F97316',
    textOnPrimary: '#FFFFFF',
  },
  {
    id: 'slate',
    name: 'Gris Elegante',
    description: 'Sofisticado y neutral',
    primaryColor: '#475569', // Already compliant (ratio: 7.37:1)
    secondaryColor: '#1E293B',
    accentColor: '#64748B',
    textOnPrimary: '#FFFFFF',
  },
];

type CustomizationMode = 'preset' | 'advanced';

const Branding: React.FC = () => {
  const { theme, updateTheme } = useTheme();
  const { branding, isLoading, updateBranding, uploadLogo, isUpdating, isUploading } = useBranding();
  
  const [mode, setMode] = useState<CustomizationMode>('preset');
  const [selectedPreset, setSelectedPreset] = useState<string>('blue');
  const [formData, setFormData] = useState(theme);
  const [logoPreview, setLogoPreview] = useState<string | undefined>(theme.logoUrl);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [unsavedChanges, setUnsavedChanges] = useState<typeof formData | null>(null);
  const [showCompanyName, setShowCompanyName] = useState<boolean>(theme.showCompanyName);

  // Load branding data when available
  useEffect(() => {
    if (branding && !isLoading) {
      setFormData({
        primaryColor: branding.primaryColor,
        secondaryColor: branding.secondaryColor,
        accentColor: branding.accentColor,
        textOnPrimary: branding.textOnPrimary,
        logoUrl: branding.logoUrl,
        showCompanyName: branding.showCompanyName,
      });
      setLogoPreview(branding.logoUrl);
      setShowCompanyName(branding.showCompanyName);
    }
  }, [branding, isLoading]);

  // Restore unsaved changes if they exist
  useEffect(() => {
    if (unsavedChanges) {
      setFormData(prev => ({ ...prev, ...unsavedChanges }));
      toast('Cambios no guardados restaurados', { icon: '💾' });
    }
  }, [unsavedChanges]);

  const handleColorChange = (field: keyof typeof formData, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const handlePresetSelect = (presetId: string) => {
    const preset = PRESET_THEMES.find(p => p.id === presetId);
    if (preset) {
      setSelectedPreset(presetId);
      setFormData({
        ...formData,
        primaryColor: preset.primaryColor,
        secondaryColor: preset.secondaryColor,
        accentColor: preset.accentColor,
        textOnPrimary: preset.textOnPrimary,
      });
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        // Validate file
        validateLogoFile(file);
        
        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoPreview(reader.result as string);
          setLogoFile(file);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        if (error instanceof FileValidationError) {
          toast.error(error.message);
        } else {
          toast.error('Error al cargar el archivo');
        }
      }
    }
  };

  const handleSave = async () => {
    try {
      // Validate hex colors
      if (!validateHexColor(formData.primaryColor)) {
        toast.error('Color primario inválido. Debe ser formato #RRGGBB');
        return;
      }
      if (!validateHexColor(formData.secondaryColor)) {
        toast.error('Color secundario inválido. Debe ser formato #RRGGBB');
        return;
      }
      if (!validateHexColor(formData.accentColor)) {
        toast.error('Color de acento inválido. Debe ser formato #RRGGBB');
        return;
      }
      if (!validateHexColor(formData.textOnPrimary)) {
        toast.error('Color de texto inválido. Debe ser formato #RRGGBB');
        return;
      }

      // Upload logo if changed
      let logoUrl = formData.logoUrl;
      if (logoFile) {
        const uploadResult = await uploadLogo(logoFile);
        logoUrl = uploadResult.logoUrl;
      }

      // Update branding
      const updatedBranding = await updateBranding({
        primaryColor: formData.primaryColor,
        secondaryColor: formData.secondaryColor,
        accentColor: formData.accentColor,
        textOnPrimary: formData.textOnPrimary,
        showCompanyName: showCompanyName,
      });

      // Update theme context
      updateTheme({
        ...updatedBranding,
        logoUrl: logoUrl || updatedBranding.logoUrl,
        showCompanyName: showCompanyName,
      });

      // Clear unsaved changes
      setUnsavedChanges(null);
      setLogoFile(null);
      
      toast.success('Personalización guardada correctamente');
    } catch (error) {
      console.error('Error saving branding:', error);
      
      // Preserve unsaved changes
      setUnsavedChanges(formData);
      
      const errorMessage = error instanceof Error ? error.message : 'Error al guardar la personalización';
      toast.error(errorMessage);
    }
  };

  const handleReset = () => {
    const defaultTheme = PRESET_THEMES[0];
    setFormData({
      primaryColor: defaultTheme.primaryColor,
      secondaryColor: defaultTheme.secondaryColor,
      accentColor: defaultTheme.accentColor,
      textOnPrimary: defaultTheme.textOnPrimary,
      showCompanyName: true,
    });
    setSelectedPreset('blue');
    setShowCompanyName(true);
    setLogoPreview(undefined);
    setLogoFile(null);
    setUnsavedChanges(null);
  };

  const hasChanges = 
    formData.primaryColor !== (branding?.primaryColor || theme.primaryColor) ||
    formData.secondaryColor !== (branding?.secondaryColor || theme.secondaryColor) ||
    formData.accentColor !== (branding?.accentColor || theme.accentColor) ||
    formData.textOnPrimary !== (branding?.textOnPrimary || theme.textOnPrimary) ||
    showCompanyName !== (branding?.showCompanyName ?? theme.showCompanyName) ||
    logoFile !== null;

  const isSaving = isUpdating || isUploading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Personalización</h1>
        <p className="mt-2 text-sm text-gray-600">
          Configura el logo y los colores de tu empresa
        </p>
      </div>

      {/* Mode Selector */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={() => setMode('preset')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                mode === 'preset'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Palette className="w-5 h-5" />
              <span>Paletas Predefinidas</span>
            </button>
            <button
              onClick={() => setMode('advanced')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                mode === 'advanced'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Sliders className="w-5 h-5" />
              <span>Personalización Avanzada</span>
            </button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Logo Section */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Logo de la Empresa</h2>
            
            <div className="space-y-4">
              {/* Logo Preview */}
              <div className="flex items-center justify-center w-full h-48 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300">
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="max-h-full max-w-full object-contain p-4"
                  />
                ) : (
                  <div className="text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">Sin logo</p>
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <div>
                <label className="block">
                  <span className="sr-only">Elegir logo</span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                    onChange={handleLogoChange}
                    disabled={isSaving}
                    className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-md file:border-0
                      file:text-sm file:font-semibold
                      file:bg-blue-50 file:text-blue-700
                      hover:file:bg-blue-100
                      cursor-pointer
                      disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </label>
                <p className="mt-2 text-xs text-gray-500">
                  PNG, JPG o SVG. Máximo 2MB. Recomendado: 200x200px
                </p>
              </div>

              {/* Show Company Name Toggle */}
              <div className="pt-4 border-t border-gray-200">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      Mostrar nombre de empresa
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Muestra el nombre de tu empresa junto al logo
                    </p>
                  </div>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={showCompanyName}
                      onChange={(e) => setShowCompanyName(e.target.checked)}
                      disabled={isSaving}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                  </div>
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Colors Section */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Colores Corporativos</h2>
            
            {mode === 'preset' ? (
              /* Preset Mode */
              <div className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  Selecciona una paleta de colores profesional
                </p>
                <div className="space-y-3">
                  {PRESET_THEMES.map((preset) => (
                    <button
                      key={preset.id}
                      onClick={() => handlePresetSelect(preset.id)}
                      disabled={isSaving}
                      className={`w-full p-4 rounded-lg border-2 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed ${
                        selectedPreset === preset.id
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{preset.name}</h3>
                          <p className="text-sm text-gray-500">{preset.description}</p>
                        </div>
                        <div className="flex space-x-2 ml-4">
                          <div
                            className="w-8 h-8 rounded border border-gray-200"
                            style={{ backgroundColor: preset.primaryColor }}
                            title="Primario"
                          />
                          <div
                            className="w-8 h-8 rounded border border-gray-200"
                            style={{ backgroundColor: preset.secondaryColor }}
                            title="Secundario"
                          />
                          <div
                            className="w-8 h-8 rounded border border-gray-200"
                            style={{ backgroundColor: preset.accentColor }}
                            title="Acento"
                          />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* Advanced Mode */
              <div className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  Personaliza cada color individualmente
                </p>
                
                {/* Primary Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color Primario
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={formData.primaryColor}
                      onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                      disabled={isSaving}
                      className="h-12 w-20 rounded border border-gray-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <input
                      type="text"
                      value={formData.primaryColor}
                      onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                      disabled={isSaving}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="#3B82F6"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Usado en el sidebar y elementos principales
                  </p>
                </div>

                {/* Secondary Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color Secundario
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={formData.secondaryColor}
                      onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                      disabled={isSaving}
                      className="h-12 w-20 rounded border border-gray-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <input
                      type="text"
                      value={formData.secondaryColor}
                      onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                      disabled={isSaving}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="#1E40AF"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Usado en elementos destacados
                  </p>
                </div>

                {/* Accent Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color de Acento
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={formData.accentColor}
                      onChange={(e) => handleColorChange('accentColor', e.target.value)}
                      disabled={isSaving}
                      className="h-12 w-20 rounded border border-gray-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <input
                      type="text"
                      value={formData.accentColor}
                      onChange={(e) => handleColorChange('accentColor', e.target.value)}
                      disabled={isSaving}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="#60A5FA"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Usado en elementos activos y hover
                  </p>
                </div>

                {/* Text on Primary */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color de Texto sobre Primario
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={formData.textOnPrimary}
                      onChange={(e) => handleColorChange('textOnPrimary', e.target.value)}
                      disabled={isSaving}
                      className="h-12 w-20 rounded border border-gray-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <input
                      type="text"
                      value={formData.textOnPrimary}
                      onChange={(e) => handleColorChange('textOnPrimary', e.target.value)}
                      disabled={isSaving}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="#FFFFFF"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Color del texto en el sidebar
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Preview Section */}
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Vista Previa</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Sidebar Preview */}
            <div
              className="rounded-lg p-4 space-y-3"
              style={{
                backgroundColor: formData.primaryColor,
                color: formData.textOnPrimary,
              }}
            >
              <div className="flex items-center space-x-3">
                {logoPreview ? (
                  <img 
                    src={logoPreview} 
                    alt="Logo" 
                    className={`object-contain flex-shrink-0 transition-all ${
                      showCompanyName ? 'w-14 h-14' : 'w-24 h-24'
                    }`}
                  />
                ) : (
                  <div
                    className={`rounded flex items-center justify-center font-bold flex-shrink-0 transition-all ${
                      showCompanyName ? 'w-14 h-14 text-xl' : 'w-24 h-24 text-4xl'
                    }`}
                    style={{ backgroundColor: formData.secondaryColor }}
                  >
                    K
                  </div>
                )}
                {showCompanyName && (
                  <div className="flex flex-col overflow-hidden">
                    <span className="font-bold text-lg leading-tight truncate">Mi Empresa</span>
                  </div>
                )}
              </div>
              <div
                className="px-3 py-2 rounded"
                style={{ backgroundColor: formData.accentColor }}
              >
                Menú Activo
              </div>
              <div className="px-3 py-2 opacity-80">Menú Normal</div>
            </div>

            {/* Button Preview */}
            <div className="space-y-3">
              <button
                className="w-full px-4 py-2 rounded-lg font-medium transition-transform hover:scale-105"
                style={{
                  backgroundColor: formData.primaryColor,
                  color: formData.textOnPrimary,
                }}
              >
                Botón Primario
              </button>
              <button
                className="w-full px-4 py-2 rounded-lg font-medium transition-transform hover:scale-105"
                style={{
                  backgroundColor: formData.secondaryColor,
                  color: formData.textOnPrimary,
                }}
              >
                Botón Secundario
              </button>
              <button
                className="w-full px-4 py-2 rounded-lg font-medium transition-transform hover:scale-105"
                style={{
                  backgroundColor: formData.accentColor,
                  color: formData.textOnPrimary,
                }}
              >
                Botón Acento
              </button>
            </div>

            {/* Card Preview */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">Card de Ejemplo</h3>
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${formData.primaryColor}20` }}
                >
                  <div
                    className="w-5 h-5 rounded"
                    style={{ backgroundColor: formData.primaryColor }}
                  />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">$1,234,567</p>
              <p className="text-sm text-gray-500 mt-1">Ejemplo de datos</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-end space-x-3">
        <Button
          variant="outline"
          onClick={handleReset}
          disabled={!hasChanges || isSaving}
          className="gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          Restablecer
        </Button>
        <Button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className="gap-2"
        >
          <Save className="w-4 h-4" />
          {isSaving ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </div>
    </div>
  );
};

export default Branding;
